import { DndContext } from '@dnd-kit/core';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RescheduleButton } from 'shared/components/connected/reschedule-button';
import { TaskCard } from 'shared/components/connected/task-card';
import { OverlayComponentWrapper } from 'shared/components/ui/overlay-component-wrapper';
import { SortableOverlay } from 'shared/components/ui/sortable-overlay';
import { TaskCardTaskSection } from 'shared/components/ui/task-card-task-section';
import { halfHour } from 'shared/constants';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useActiveTaskId } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useDroppableDayPartControls } from 'shared/hooks/use-droppable-day-part-controls';
import { useHandleTimeSectionDrop } from 'shared/hooks/use-handle-time-section-drop';
import { useMouseOutside } from 'shared/hooks/use-mouse-outside';
import { useSectionCollisionDetection } from 'shared/hooks/use-section-collision-detection';
import { useSortableSensors } from 'shared/hooks/use-sortable-sensors';
import { useUser } from 'shared/hooks/use-user';
import { DayPartSections } from 'shared/types/page-sections';
import { DueDisplayOptions } from 'shared/types/task-base';
import { getTimeFormat } from 'shared/utils/get-time-format';
import { noCollisionDetection } from 'shared/utils/no-collision-detection';

import { useDayPartTaskSections } from '../../hooks/use-day-part-task-sections';
import { useFullCalendarDraggableTasks } from '../../hooks/use-full-calendar-draggable-tasks';
import { SectionHeaderToday } from '../section-header-today';
import * as Styled from './day-part-grouping-tasks-list.style';

const displayDue = [DueDisplayOptions.Past, DueDisplayOptions.Future];

export const DayPartGroupingTasksList: React.FC = () => {
  const { t } = useTranslation();
  const today = useToday();
  const user = useUser();
  const containerRef = useRef<HTMLDivElement>(null);

  const openPremiumDialog = useOpenPremiumDialog();

  const { taskCardTasks, sections: initialSections } = useDayPartTaskSections();
  const onDragEnd = useHandleTimeSectionDrop(initialSections);
  const {
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    activeId,
    sections,
    setSections,
  } = useDroppableDayPartControls({ initialSections, onDragEnd });
  const sensors = useSortableSensors();
  const collisionStrategy = useSectionCollisionDetection(activeId, sections);

  const selectedTask = useActiveTaskId();

  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();

  const activeItem = useMemo(
    () =>
      activeId ? taskCardTasks.find(({ id }) => id === activeId) : undefined,
    [activeId, taskCardTasks],
  );

  const tasks = useMemo(
    () => taskCardTasks.map(({ raw }) => raw.task),
    [taskCardTasks],
  );
  useFullCalendarDraggableTasks(tasks, containerRef);

  const onMouseLeave = useCallback(
    () => setSections(initialSections),
    [initialSections, setSections],
  );

  const isOutsideList = useMouseOutside(containerRef, {
    enabled: !!activeId,
    onMouseLeave,
  });

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragMove={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      sensors={sensors}
      collisionDetection={
        isOutsideList ? noCollisionDetection : collisionStrategy
      }
    >
      <Styled.Container ref={containerRef}>
        {sections.map(({ id, items, startTime }) => (
          <Styled.Section key={id}>
            <TaskCardTaskSection
              date={today}
              startTime={startTime}
              duration={id !== DayPartSections.AllDay ? halfHour : undefined}
              tasks={items}
              displayDue={displayDue}
              tagIds={user?.settings?.todayFilterByTags}
              timeFormat={getTimeFormat(user)}
              selectedTask={selectedTask}
              goals={goals}
              lifeAreas={lifeAreas}
              customHeader={
                <SectionHeaderToday
                  title={t(
                    id === DayPartSections.Overdue
                      ? 'general.sections.overdue.title'
                      : `general.sections.day-part.${id}.title`,
                  )}
                  actions={
                    id === DayPartSections.Overdue
                      ? [
                          <RescheduleButton
                            tasks={items.map(({ raw }) => raw.task)}
                          />,
                        ]
                      : undefined
                  }
                />
              }
              droppableId={id}
              onPremium={openPremiumDialog}
              canCreateTask={id !== DayPartSections.Overdue}
              canCollapse
              canReorder
            />
          </Styled.Section>
        ))}
      </Styled.Container>

      <SortableOverlay>
        {!!activeItem && (
          <OverlayComponentWrapper isOutside={isOutsideList}>
            <TaskCard task={activeItem.raw.task} displayDue={displayDue} />
          </OverlayComponentWrapper>
        )}
      </SortableOverlay>
    </DndContext>
  );
};
